/**
 * Terminal State Utilities
 * 
 * Centralized utilities for detecting and handling terminal boot states
 * to prevent race conditions with late timers
 */

import { bootStateMachine, BootPhase } from './BootStateMachine';

/**
 * Terminal phases that indicate boot process is complete
 */
const TERMINAL_PHASES: Set<BootPhase> = new Set(['LICENSED', 'UNLICENSED', 'ERROR']);

/**
 * Phases that indicate auth is ready or beyond
 */
const AUTH_READY_OR_BEYOND: Set<BootPhase> = new Set([
  'AUTH_READY', 'LICENSE_CHECK', 'LICENSED', 'UNLICENSED', 'ERROR'
]);

/**
 * Check if the boot state machine is in a terminal state
 */
export const isTerminalState = (): boolean => {
  const currentPhase = bootStateMachine.getPhase();
  return TERMINAL_PHASES.has(currentPhase);
};

/**
 * Check if the boot state machine has reached AUTH_READY or beyond
 */
export const isAuthReadyOrBeyond = (): boolean => {
  const currentPhase = bootStateMachine.getPhase();
  return AUTH_READY_OR_BEYOND.has(currentPhase);
};

/**
 * Get the current boot phase
 */
export const getCurrentPhase = (): BootPhase => {
  return bootStateMachine.getPhase();
};

/**
 * Subscribe to terminal state changes and execute a callback
 * Returns an unsubscribe function
 */
export const onTerminalState = (callback: (phase: BootPhase) => void): () => void => {
  return bootStateMachine.subscribe((state) => {
    if (TERMINAL_PHASES.has(state.phase)) {
      callback(state.phase);
    }
  });
};

/**
 * Subscribe to auth ready or beyond state changes and execute a callback
 * Returns an unsubscribe function
 */
export const onAuthReadyOrBeyond = (callback: (phase: BootPhase) => void): () => void => {
  return bootStateMachine.subscribe((state) => {
    if (AUTH_READY_OR_BEYOND.has(state.phase)) {
      callback(state.phase);
    }
  });
};

/**
 * Log terminal state cancellation for consistency
 */
export const logTerminalCancellation = (component: string, action: string, phase: BootPhase): void => {
  console.log(`[${component.toUpperCase()}] ${action} cancelled on terminal (${phase})`, {
    ts: Date.now(),
    phase,
    terminal: true
  });
};